// src/Components/Forms/Financement/Salam/Paiement/PaiementSalam.tsx
import { useState, useEffect } from 'react';
import { Tabs, Space } from 'antd';
import Table from '../../../../TableResizable';
import type { TabsProps } from 'antd';
import { Button, Form, Spin } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import axios from 'axios';
import Tab1 from './Tab1';
import Tab2 from './Tab2';
import Utilisateur from '../../../../../Models/Utilisateur';
import { useAuthHeader, useAuthUser } from '../../../../../Services/Auth/sessionAuth';
import Data from '../../../../../Data/Data.json';
import SweetAlert from 'sweetalert2';
import { Crypter } from '../../../../../Functions/Security/Crypter';
import { Typography } from '@mui/material';
import { ColumnsType } from 'antd/es/table';
import formatNumber from '../../../../../Functions/Traitement/FormatNumber';
import ParamsPicklist from '../../../../../Types/ParamsPicklist';
import ReformatDate from '../../../../../Functions/Traitement/ReformatDate';

const PaiementSalam = () => {
  const auth = useAuthUser();
  const user: Utilisateur = JSON.parse(JSON.stringify(auth()));
  const authHeader = useAuthHeader();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [pageLoad, setPageLoad] = useState(true);
  const [data, setData] = useState<any[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [historiquePaiements, setHistoriquePaiements] = useState<any[]>([]);
  const [modifActve, setModifActve] = useState(true);
  const [enregActve, setEnregActve] = useState(true);
  const [annulActive, setAnnulActive] = useState(true);

  // ✅ Chargement du libellé du gestionnaire
  const loadGestionnaire = (codeGes: string) => {
    if (!codeGes) {
      form.setFieldValue('lib_CODGEA', '');
      return;
    }

    const searchParams: ParamsPicklist = {
      CodeAgence: user.codage,
      Table: 'GESTIONNAIRE',
      Colonne1: 'CODGES',
      Colonne2: 'LIBELLE',
      Init: codeGes,
      Colagence: 'CODAGE',
      Choix: 22,
    };

    const config = {
      method: 'get',
      url: Data.ApiUrl.AnySearch,
      headers: {
        'Content-Type': 'application/json',
        Authorization: authHeader(),
        CypherSearch: Crypter.encrypt(JSON.stringify(searchParams)),
      },
    };

    axios
      .request(config)
      .then((response) => {
        if (response.data) {
          const res = JSON.parse(Crypter.decrypt(response.data)).Value;
          if (res.StatutCode === 200) {
            const libelle = res.Result.Table?.[0]?.LIBELLE || '';
            form.setFieldValue('lib_CODGEA', libelle);
          }
        }
      })
      .catch((error) => {
        console.error("Erreur lors du chargement du gestionnaire :", error);
        form.setFieldValue('lib_CODGEA', '');
      });
  };

  const loadFinancement = () => {
    setPageLoad(false);
    setLoading(true);
    setData([]);

    const searchParams: ParamsPicklist = {
      CodeAgence: user.codage,
      Table: 'view_AnySuiviPaiementSalam',
      Colonne1: 'EtapeActuelle',
      Colonne2: '',
      Init: 'PAIEMENT',
      Colagence: 'CODAGE',
      Choix: 34,
    };

    const config = {
      method: 'get',
      url: Data.ApiUrl.AnySearch,
      headers: {
        'Content-Type': 'application/json',
        Authorization: authHeader(),
        CypherSearch: Crypter.encrypt(JSON.stringify(searchParams)),
      },
    };

    axios
      .request(config)
      .then((response) => {
        if (response.data) {
          const res = JSON.parse(Crypter.decrypt(response.data)).Value;
          if (res.StatutCode === 200) {
            const datas = res.Result.Table || [];
            if (datas.length === 0) {
              SweetAlert.fire({
                title: res.Message || 'Aucun financement Salam en attente de paiement',
                text: res.Result || 'Tous les financements ont été payés ou ne sont pas encore validés.',
                icon: 'info',
              });
            } else {
              const newData = datas.map((item: any, index: number) => ({
                key: index,
                DateVal: item.DateVal ? new Date(item.DateVal).toLocaleDateString('fr') : '',
                NumeroDemande: item.NumeroDemande,
                NumeroFinancement: item.NumeroFinancement,
                NUMCLI: item.Numcli,
                NOMCLI: item.Nomcli,
                CodeSalam: item.CodeSalam,
                CODGEA: item.CODGEA,
                MontantValide: item.MontantValide,
                TotalPaye: item.TotalPaye,
                ResteAPayer: item.ResteAPayer,
              }));
              setData(newData);
            }
          }
        }
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        SweetAlert.fire({
          title: 'Erreur',
          text: error.toString(),
          icon: 'error',
        });
      });
  };

  useEffect(() => {
    if (pageLoad) loadFinancement();
  }, [pageLoad]);

  const columns: ColumnsType<any> = [
    { title: 'Date Validation', dataIndex: 'DateVal' },
    { title: 'N° Demande', dataIndex: 'NumeroDemande' },
    { title: 'N° Financement', dataIndex: 'NumeroFinancement' },
    { title: 'N° Client', dataIndex: 'NUMCLI' },
    { title: 'Nom Client', dataIndex: 'NOMCLI' },
    { title: 'Type Salam', dataIndex: 'CodeSalam' },
    { title: 'Gestionnaire', dataIndex: 'CODGEA' },
    { title: 'Mtn Validé', dataIndex: 'MontantValide', render: (v) => formatNumber(v) },
    { title: 'Mtn Payé', dataIndex: 'TotalPaye', render: (v) => formatNumber(v) },
    { title: 'Reste à payer', dataIndex: 'ResteAPayer', render: (v) => formatNumber(v) },
  ];

  const handleDemandeSalam = (numeroDemande: string) => {
    setLoading(true);
    const encryptedNum = Crypter.encrypt(numeroDemande);
    const config = {
      method: 'POST',
      url: Data.ApiUrl.ReadFinancementSalam,
      headers: {
        Authorization: authHeader(),
        'Content-Type': 'application/json',
        CypherNumeroDemandeSalam: encryptedNum,
        CypherAction: Crypter.encrypt('READ_PAIEMENT'),
      },
    };
    axios
      .request(config)
      .then((response) => {
        setLoading(false);
        const res = JSON.parse(Crypter.decrypt(response.data)).Value;
        if (res.StatutCode === 200) {
          const dataFinancement = res.Result.Table1?.[0] || {};
          const codeGes = dataFinancement.CODGEA || '';

          form.setFieldsValue({
            ...dataFinancement,
            Quantite: dataFinancement.Quantite || 0,
            PrixUnitaire: dataFinancement.PrixUnitaire || 0,
            MontantFinancement: dataFinancement.MontantFinancement || 0,
            MontantValide: dataFinancement.MontantValide || 0,
            TotalPaye: dataFinancement.TotalPaye || 0,
            ResteAPayer: dataFinancement.ResteAPayer || 0,
            DateDemande: dataFinancement.DateDemande ? ReformatDate(dataFinancement.DateDemande) : '',
            DateVal: dataFinancement.DateVal ? ReformatDate(dataFinancement.DateVal) : '',
            DateLivraison: dataFinancement.DateLivraisonPrevue ? ReformatDate(dataFinancement.DateLivraisonPrevue) : '',
            StatutFinancement: dataFinancement.StatutFinancement || '',
            EtapeActuelle: dataFinancement.EtapeActuelle || '',
          });

          // ✅ Charger le libellé du gestionnaire
          loadGestionnaire(codeGes);

          const paiements = res.Result.Table2 || [];
          if (paiements.length > 0) {
            const mapped = paiements.map((p: any) => ({
              ...p,
              DatePaiement: p.DatePaiement ? new Date(p.DatePaiement).toLocaleDateString('fr') : '',
              MontantPaye: p.MontantPaye,
            }));
            setHistoriquePaiements(mapped);
          } else {
            setHistoriquePaiements([]);
          }
          setModifActve(false);
          setEnregActve(false);
          setAnnulActive(false);
        } else {
          SweetAlert.fire({
            title: res.Message || 'Financement non trouvé',
            text: res.Result || 'Vérifiez le numéro de demande.',
            icon: 'warning',
          });
        }
      })
      .catch((error) => {
        setLoading(false);
        SweetAlert.fire({
          title: 'Erreur',
          text: error.toString(),
          icon: 'error',
        });
      });
  };

  const onSelectChange = (newSelectedRowKeys: any) => {
    const index = Array.isArray(newSelectedRowKeys) ? newSelectedRowKeys[0] : newSelectedRowKeys;
    if (index !== undefined && data[index]) {
      const record = data[index];
      if (record.NumeroDemande) {
        form.setFieldsValue(record);
        handleDemandeSalam(record.NumeroDemande);
      } else {
        SweetAlert.fire({
          title: 'Numéro de demande manquant',
          text: 'Impossible de charger les détails de ce financement.',
          icon: 'warning',
        });
      }
    }
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const handlePayer = () => {
    const values = form.getFieldsValue();
    const montantAPayer = values.MontantADecaisser;
    const resteAPayer = values.ResteAPayer;

    if (!montantAPayer || montantAPayer <= 0) {
      SweetAlert.fire({
        title: 'Montant invalide',
        text: 'Veuillez saisir un montant supérieur à zéro.',
        icon: 'warning',
      });
      return;
    }
    if (montantAPayer > resteAPayer) {
      SweetAlert.fire({
        title: 'Montant trop élevé',
        text: `Le montant saisi (${formatNumber(montantAPayer)}) dépasse le reste à payer (${formatNumber(resteAPayer)}).`,
        icon: 'warning',
      });
      return;
    }

    const payload = {
      NumeroDemande: values.NumeroDemande,
      MontantAPayer: montantAPayer,
      RaisonPaiement: values.RaisonDecaisse || '',
      CodeUti: user.coduti,
    };

    SweetAlert.fire({
      title: `Paiement pour la demande ${values.NumeroDemande}`,
      text: `Montant à payer : ${formatNumber(montantAPayer)} XAF\nReste à payer : ${formatNumber(resteAPayer)} XAF`,
      showCancelButton: true,
      confirmButtonText: 'Confirmer le paiement',
      cancelButtonText: 'Annuler',
      icon: 'question',
      confirmButtonColor: 'green',
      cancelButtonColor: 'darkred',
    }).then((result) => {
      if (result.isConfirmed) {
        setLoading(true);
        const encryptedData = Crypter.encrypt(JSON.stringify(payload));
        const config = {
          method: 'POST',
          url: Data.ApiUrl.DecaissementSalam,
          headers: {
            Authorization: authHeader(),
            'Content-Type': 'application/json',
          },
          data: encryptedData,
        };
        axios
          .request(config)
          .then((response) => {
            setLoading(false);
            const res = JSON.parse(Crypter.decrypt(response.data)).Value;
            if (res.StatutCode === 200) {
              SweetAlert.fire({
                title: 'Paiement effectué',
                text: res.Message || res.Result,
                icon: 'success',
              });
              handleAnnul();
              loadFinancement();
            } else {
              SweetAlert.fire({
                title: res.Message || 'Erreur',
                text: res.Result || "Une erreur s'est produite.",
                icon: 'warning',
              });
            }
          })
          .catch((error) => {
            setLoading(false);
            SweetAlert.fire({
              title: 'Erreur',
              text: error.toString(),
              icon: 'error',
            });
          });
      }
    });
  };

  const handleAnnul = () => {
    form.resetFields();
    setHistoriquePaiements([]);
    setSelectedRowKeys([]);
    setModifActve(true);
    setEnregActve(true);
    setAnnulActive(true);
  };

  const items: TabsProps['items'] = [
    {
      key: '1',
      label: <span><UserOutlined /> Détail du financement</span>,
      children: <Tab1 form={form} />,
    },
    {
      key: '2',
      label: <span><UserOutlined /> Historique des paiements</span>,
      children: <Tab2 data={historiquePaiements} />,
    },
  ];

  return (
    <div className="tab">
      <Typography align="center" className="formTitle">
        <b>PAIEMENT SALAM</b>
      </Typography>
      <Spin spinning={loading} size="large" style={{ color: 'green' }}>
        <Form
          form={form}
          autoComplete="off"
          labelAlign="left"
          style={{ minWidth: 'auto', maxWidth: 'auto' }}
        >
          <div className="bG">
            <legend>
              <Typography color="darkgreen">Actions</Typography>
            </legend>
            <Form.Item shouldUpdate className="bouttong">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button
                  size="large"
                  className="bt_add"
                  disabled={enregActve}
                  onClick={handlePayer}
                  type="ghost"
                  htmlType="button"
                  block
                  style={{ backgroundColor: 'green', color: 'white' }}
                >
                  Payer
                </Button>
                <Button
                  size="large"
                  className="bt_add"
                  disabled={annulActive}
                  onClick={handleAnnul}
                  type="ghost"
                  htmlType="button"
                  block
                >
                  Annuler
                </Button>
              </Space>
            </Form.Item>
          </div>
          <div className="clientP">
            <Tabs size="small" type="card" defaultActiveKey="1" items={items} />
            <div className="border-left tabElementAuto">
              <Spin spinning={loading}>
                <Table
                  size="small"
                  style={{ width: 'auto' }}
                  rowSelection={{ ...rowSelection, type: 'radio' }}
                  columns={columns}
                  dataSource={data}
                  scroll={{ y: 190, x: 10 }}
                  pagination={false}
                />
              </Spin>
            </div>
          </div>
        </Form>
      </Spin>
    </div>
  );
};

export default PaiementSalam;